import { Link } from "react-router-dom";
import { Palette, FileText } from "lucide-react";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-hero-gradient">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-semibold text-xl text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                ColorWonder
              </span>
            </Link>
          </div>
        </div>
      </nav>

      <div className="pt-24 pb-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-slate-100">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-violet-600" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-slate-800" style={{ fontFamily: "'Fredoka', sans-serif" }}>
                Terms of Service
              </h1>
            </div>
            
            <p className="text-slate-500 mb-8">Last updated: January 26, 2025</p>

            <div className="prose prose-slate max-w-none">
              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">1. Acceptance of Terms</h2>
                <p className="text-slate-600 leading-relaxed">
                  By accessing or using ColorWonder AI ("Service"), you agree to be bound by these Terms of Service. 
                  If you disagree with any part of these terms, you may not access the Service.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">2. Description of Service</h2>
                <p className="text-slate-600 leading-relaxed">
                  ColorWonder AI is an AI-powered coloring book generator that creates printable coloring pages based on 
                  user prompts. The Service uses artificial intelligence to generate unique line art suitable for coloring.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">3. User Accounts</h2>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>You must provide accurate and complete information when creating an account</li>
                  <li>You are responsible for maintaining the security of your account credentials</li>
                  <li>You must be at least 13 years old to use the Service</li>
                  <li>One free account per person/household is allowed</li>
                  <li>We reserve the right to suspend or terminate accounts that violate these terms</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">4. Subscription Plans and Payments</h2>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li><strong>Free Plan:</strong> Limited to 5 pages per month, 2 pages per day</li>
                  <li><strong>Paid Plans:</strong> Subscription plans provide additional page generation limits</li>
                  <li>All payments are processed securely through Stripe</li>
                  <li>Subscriptions automatically renew unless cancelled</li>
                  <li>Prices may change with 30 days notice</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">5. Intellectual Property Rights</h2>
                <p className="text-slate-600 leading-relaxed mb-4">
                  <strong>Your Content:</strong> You retain ownership of the prompts you create and have rights to use 
                  the generated coloring pages for personal use.
                </p>
                <p className="text-slate-600 leading-relaxed mb-4">
                  <strong>Commercial License:</strong> Plus and Pro plan subscribers receive a commercial license to 
                  use generated content for commercial purposes.
                </p>
                <p className="text-slate-600 leading-relaxed">
                  <strong>Our Content:</strong> The Service, including its original content, features, and functionality, 
                  is owned by ColorWonder AI and protected by copyright and trademark laws.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">6. Acceptable Use</h2>
                <p className="text-slate-600 leading-relaxed mb-4">You agree NOT to:</p>
                <ul className="list-disc pl-6 text-slate-600 space-y-2">
                  <li>Generate content that is illegal, harmful, or violates third-party rights</li>
                  <li>Attempt to bypass usage limits or abuse the Service</li>
                  <li>Create multiple free accounts to circumvent restrictions</li>
                  <li>Use automated systems to access the Service without permission</li>
                  <li>Reverse engineer or attempt to extract the source code of the Service</li>
                  <li>Generate inappropriate, offensive, or adult content</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">7. Disclaimer of Warranties</h2>
                <p className="text-slate-600 leading-relaxed">
                  THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE THAT THE SERVICE 
                  WILL BE UNINTERRUPTED, SECURE, OR ERROR-FREE. AI-GENERATED CONTENT MAY VARY IN QUALITY AND MAY NOT 
                  ALWAYS MEET YOUR EXPECTATIONS.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">8. Limitation of Liability</h2>
                <p className="text-slate-600 leading-relaxed">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, COLORWONDER AI SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, 
                  SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF YOUR USE OF THE SERVICE.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">9. Changes to Terms</h2>
                <p className="text-slate-600 leading-relaxed">
                  We reserve the right to modify these terms at any time. We will notify users of significant changes 
                  via email or through the Service. Continued use of the Service after changes constitutes acceptance.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">10. Governing Law</h2>
                <p className="text-slate-600 leading-relaxed">
                  These Terms shall be governed by and construed in accordance with applicable laws, without regard to 
                  conflict of law principles.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-xl font-semibold text-slate-800 mb-3">11. Contact Us</h2>
                <p className="text-slate-600 leading-relaxed">
                  For questions about these Terms, please contact us at:{" "}
                  <a href="mailto:legal@colorwonder.app" className="text-violet-600 hover:underline">
                    legal@colorwonder.app
                  </a>
                </p>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
